import { useApolloClient } from '@apollo/client';
import { Formik, type FormikProps } from 'formik';
import { useCallback, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type ModelConfiguratorSpecsFragment } from '../../../api/fragments/ModelConfiguratorSpecs';
import { type VariantConfiguratorSpecsFragment } from '../../../api/fragments/VariantConfiguratorSpecs';
import {
    CreateVariantConfiguratorDocument,
    type CreateVariantConfiguratorMutation,
    type CreateVariantConfiguratorMutationVariables,
} from '../../../api/mutations/createVariantConfigurator';
import { ModuleType, VehicleKind } from '../../../api/types';
import Form from '../../../components/fields/Form';
import VehicleSelectField from '../../../components/fields/VehicleSelectField';
import { useThemeComponents } from '../../../themes';
import useHandleError from '../../../utilities/useHandleError';

type CreateVariantConfiguratorModalProps = {
    isVisible: boolean;
    onSuccess: (variantConfigurator: VariantConfiguratorSpecsFragment) => void;
    onCancel: () => void;
    modelConfigurator: ModelConfiguratorSpecsFragment;
};

type FormValues = {
    variantId: string;
};

const FORM_NAME = 'addVariantConfigurator';

const AddVariantConfiguratorModal = ({
    isVisible,
    onSuccess,
    onCancel,
    modelConfigurator,
}: CreateVariantConfiguratorModalProps) => {
    const { t } = useTranslation(['modelConfiguratorDetails']);
    const { Modal } = useThemeComponents();

    const formRef = useRef<FormikProps<FormValues>>();

    const [isSubmitting, setSubmitting] = useState(false);

    const apolloClient = useApolloClient();

    const handleOkay = useCallback(() => {
        setSubmitting(true);

        formRef.current.handleSubmit();
    }, []);

    const onSubmit = useHandleError<{ variantId: string }>(
        async values => {
            const { data } = await apolloClient
                .mutate<CreateVariantConfiguratorMutation, CreateVariantConfiguratorMutationVariables>({
                    mutation: CreateVariantConfiguratorDocument,
                    variables: {
                        modelConfiguratorId: modelConfigurator.id,
                        variantId: values.variantId,
                    },
                })
                .finally(() => {
                    setSubmitting(false);
                });

            if (data.variantConfigurator) {
                onSuccess(data?.variantConfigurator);
            }
        },
        [apolloClient, modelConfigurator.id, onSuccess]
    );

    const vehicleModuleId = useMemo(
        () =>
            modelConfigurator.module.__typename === ModuleType.ConfiguratorModule
                ? modelConfigurator.module.vehicleModuleId
                : null,
        [modelConfigurator.module]
    );

    return (
        <Modal
            cancelButtonProps={{ disabled: isSubmitting }}
            okButtonProps={{ loading: isSubmitting }}
            okText={t('modelConfiguratorDetails:actions.addVariantOk')}
            onCancel={onCancel}
            onOk={handleOkay}
            title={t('modelConfiguratorDetails:modals.addVariant.title')}
            visible={isVisible}
            width={480}
            centered
        >
            <Formik initialValues={{ variantId: '' }} innerRef={formRef} onSubmit={onSubmit}>
                {({ handleSubmit }) => (
                    <Form
                        id={FORM_NAME}
                        name={FORM_NAME}
                        onSubmitCapture={ev => {
                            ev.preventDefault();
                            handleSubmit(ev);
                        }}
                    >
                        {/* TODO: add exception for already added variant */}
                        <VehicleSelectField
                            {...t('modelConfiguratorDetails:modals.addVariant.fields.variant', { returnObjects: true })}
                            modelIds={[modelConfigurator.model.id]}
                            moduleId={vehicleModuleId}
                            name="variantId"
                            vehicleKind={VehicleKind.LocalVariant}
                            useSuiteId
                        />
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default AddVariantConfiguratorModal;
